// The project marker of both maps: the red Whoon pin with the white W. Artwork
// copied from public-src/img/icons/pin-fallback.svg rather than loaded from
// that URL, because maplibre rasterises at the image's intrinsic size and the
// asset declares 99x130; inlining is what lets us pick 36x47 (WHN-2132).
// Whoon red (color(brand)): blue-on-blue against the blue cluster gave too
// little contrast. The map encodes place and amount, not phase status, with one
// exception: a project without phases (no homes yet) gets the inverted pin on
// the listing map, white with a red W (WHN-2212).
export const MARKER_COLOR = '#d60925';
export const UPCOMING_MARKER_FILL = '#ffffff';
// Inner edge that lifts the white body off the light map. Drawn as a double
// width stroke clipped to the body, so it never grows the pin past the red one
// and the viewBox cannot cut off its tips.
export const UPCOMING_MARKER_EDGE_WIDTH = 6;
export const UPCOMING_MARKER_CLIP_ID = 'upcoming-marker-clip';
export const PIN_VIEWBOX = '0 0 99 130';
export const PIN_BODY_PATH =
  'M97.7067916,48.4686567 C97.7067916,77.1537313 64.8545528,115.093284 53.0321648,127.823881 C50.7672394,130.255224 46.9396275,130.260448 44.6635081,127.829851 C32.8239558,115.144776 -0.0573874667,77.3208955 0,48.4686567 C0,21.7022388 21.8747021,0 48.8538066,0 C75.8321648,0 97.7067916,21.7022388 97.7067916,48.4686567 Z';
export const PIN_W_PATH =
  'M13.3467231,2.74418604 L13.3467231,22.0782241 C13.4714588,27.9408035 12.8477801,37.794926 12.8477801,37.794926 C12.8477801,37.794926 16.0909092,28.8139534 20.2071881,20.2071881 L30.6849894,0 L41.038055,0 L41.5369978,19.832981 C41.7864694,29.1881607 41.4122621,37.6701902 41.4122621,37.6701902 C42.1606767,35.5496829 44.9048626,27.5665962 47.7737843,21.205074 L55.5073997,3.11839323 L69.3530656,3.11839323 L44.1564482,55.756871 L32.0570824,59 L31.1839323,36.2980973 C30.934461,32.3065539 30.934461,27.4418604 30.934461,24.0739957 C31.0591967,21.7040168 31.0591967,20.9556024 31.1839323,19.0845667 C30.4355179,21.205074 30.0613108,21.9534883 28.5644821,25.5708247 C26.5687103,29.9365751 22.7019027,38.5433404 20.3319238,43.2832981 L15.5919661,53.6363637 L3.24312897,51.5158564 L0,5.11416491 L13.3467231,2.74418604 Z';
// Asset is 99x130, so 36 wide comes out 47 tall
export const PIN_WIDTH = 36;
export const PIN_HEIGHT = 47;
export const MARKER_IMAGE_ID = 'project-marker';
export const SELECTED_MARKER_IMAGE_ID = 'project-marker-selected';
export const UPCOMING_MARKER_IMAGE_ID = 'upcoming-marker';
export const SELECTED_UPCOMING_MARKER_IMAGE_ID = 'upcoming-marker-selected';

// The selected pin: the same artwork with a white rim and a light grey hairline
// around it (chosen over a glow and a ground dot, 2026-09-22). The strokes need
// room outside the path, hence the padded viewBox; the icon offset pushes the
// image down by that padding so the tip stays on the coordinate and the pin
// covers the plain one underneath exactly.
const SELECTED_PIN_PAD = 12;
const SELECTED_PIN_WIDTH = Math.round((PIN_WIDTH * (99 + 2 * SELECTED_PIN_PAD)) / 99);
const SELECTED_PIN_HEIGHT = Math.round((PIN_HEIGHT * (130 + 2 * SELECTED_PIN_PAD)) / 130);
export const SELECTED_PIN_OFFSET = (SELECTED_PIN_PAD * PIN_WIDTH) / 99;
const SELECTED_RIM_EDGE = '#c9c9c9';

const projectArtwork = () =>
  `<path d="${PIN_BODY_PATH}" fill="${MARKER_COLOR}"/>` +
  `<g transform="translate(17, 22)"><path d="${PIN_W_PATH}" fill="#ffffff"/></g>`;

const upcomingArtwork = () =>
  `<clipPath id="${UPCOMING_MARKER_CLIP_ID}"><path d="${PIN_BODY_PATH}"/></clipPath>` +
  `<path d="${PIN_BODY_PATH}" fill="${UPCOMING_MARKER_FILL}" stroke="${MARKER_COLOR}" ` +
  `stroke-width="${UPCOMING_MARKER_EDGE_WIDTH * 2}" clip-path="url(#${UPCOMING_MARKER_CLIP_ID})"/>` +
  `<g transform="translate(17, 22)"><path d="${PIN_W_PATH}" fill="${MARKER_COLOR}"/></g>`;

// Deliberately without the dark outline the cluster shadow uses: a solid red
// pin already separates from the light map style on its own, and a real drop
// shadow would need a padded viewBox that moves the icon anchor.
const pinSvg = (artwork) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="${PIN_WIDTH * 2}" height="${PIN_HEIGHT * 2}" viewBox="${PIN_VIEWBOX}">` +
  `<g transform="translate(0.300671, 0.128358)">${artwork}</g></svg>`;

const selectedPinSvg = (artwork) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="${SELECTED_PIN_WIDTH * 2}" height="${SELECTED_PIN_HEIGHT * 2}" ` +
  `viewBox="${-SELECTED_PIN_PAD} ${-SELECTED_PIN_PAD} ${99 + 2 * SELECTED_PIN_PAD} ${130 + 2 * SELECTED_PIN_PAD}">` +
  `<g transform="translate(0.300671, 0.128358)">` +
  `<path d="${PIN_BODY_PATH}" fill="none" stroke="${SELECTED_RIM_EDGE}" stroke-width="22" stroke-linejoin="round"/>` +
  `<path d="${PIN_BODY_PATH}" fill="#ffffff" stroke="#ffffff" stroke-width="16" stroke-linejoin="round"/>` +
  `${artwork}</g></svg>`;

// Circle layers can't draw a pin, so the marker layers are symbol layers over
// these pre-rasterised SVGs (2x for retina). The map is read through a getter
// because the component may unmount while an image is still loading.
const loadMarkerImage = (getMap, id, svg, width, height) =>
  new Promise((resolve) => {
    const image = new Image(width * 2, height * 2);
    image.onload = () => {
      const map = getMap();
      if (map) map.addImage(id, image, { pixelRatio: 2 });
      resolve();
    };
    // A rejected promise would leave the layers unbuilt; a missing icon only
    // costs the pin, the clusters still render
    image.onerror = () => resolve();
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  });

export const addMarkerImages = (getMap) =>
  Promise.all([
    loadMarkerImage(getMap, MARKER_IMAGE_ID, pinSvg(projectArtwork()), PIN_WIDTH, PIN_HEIGHT),
    loadMarkerImage(
      getMap,
      SELECTED_MARKER_IMAGE_ID,
      selectedPinSvg(projectArtwork()),
      SELECTED_PIN_WIDTH,
      SELECTED_PIN_HEIGHT
    )
  ]);

export const addUpcomingMarkerImages = (getMap) =>
  Promise.all([
    loadMarkerImage(getMap, UPCOMING_MARKER_IMAGE_ID, pinSvg(upcomingArtwork()), PIN_WIDTH, PIN_HEIGHT),
    loadMarkerImage(
      getMap,
      SELECTED_UPCOMING_MARKER_IMAGE_ID,
      selectedPinSvg(upcomingArtwork()),
      SELECTED_PIN_WIDTH,
      SELECTED_PIN_HEIGHT
    )
  ]);
